import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MutationScope } from '@tanstack/react-query';

import collectiveApi from 'modules/common/collectiveApi';
import useLaunchpadSchema from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema';

// A request that never settles would hold every mutation queued behind it in the same scope.
const REQUEST_TIMEOUT_MS = 30_000;

interface CompleteOnboardingStepParams {
    step: string;
    data?: Record<string, unknown>;
}

const completeOnboardingStep = async (params: CompleteOnboardingStepParams) => {
    const response = await collectiveApi.post('/bookkeeping/accounting-onboarding/complete_step/', params, {
        timeout: REQUEST_TIMEOUT_MS,
    });
    return response.data;
};

// Mutations sharing a scope run one at a time, in the order they were made.
const useCompleteOnboardingStep = ({ scope }: { scope?: MutationScope } = {}) => {
    const queryClient = useQueryClient();
    const { refetchLaunchpadTasks } = useLaunchpadSchema();

    return useMutation({
        mutationFn: completeOnboardingStep,
        scope,
        onSuccess: async () => {
            console.log('✅ useCompleteOnboardingStep onSuccess: Invalidating accounting-onboarding-progress query');
            // Wait for the invalidation to complete before allowing navigation
            await queryClient.invalidateQueries({
                queryKey: ['accounting-onboarding-progress'],
                refetchType: 'all', // This will refetch both active AND inactive queries
            });
            await refetchLaunchpadTasks();
            console.log('✅ Query invalidation completed');
        },
        onError: () => {
            // Handle error
        },
    });
};

export default useCompleteOnboardingStep;
